import type {ValidationContext} from 'sanity'

/**
 * Validates that a field value is unique across all documents of a specific type
 * @param documentType - The document type to check for uniqueness (e.g., 'blog.category', 'blog.category-cluster')
 * @param fieldName - The field name to check for uniqueness (e.g., 'title', 'slug')
 * @param errorMessage - Optional custom error message
 * @returns Validation function for use in Sanity schema
 */
export function createUniqueFieldValidator(
  documentType: string,
  fieldName: string,
  errorMessage?: string,
) {
  return async (value: unknown, context: ValidationContext) => {
    if (!value) return true

    const {document, getClient} = context
    const client = getClient({apiVersion: '2024-01-01'})
    const id = document?._id?.replace(/^drafts\./, '')

    const query = `count(*[_type == $documentType && ${fieldName} == $value && !(_id in [$id, $draftId])])`
    const params = {
      documentType,
      value,
      id,
      draftId: `drafts.${id}`,
    }

    const count = await client.fetch(query, params)

    return count === 0
      ? true
      : errorMessage || `A ${documentType} with this ${fieldName} already exists`
  }
}
